import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  User,
  ChevronRight,
  Loader2,
  Check,
  ArrowLeft,
  Gift,
  Calendar,
  Bell,
  Rocket,
  Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import LegalAcceptanceForm from "../components/LegalAcceptanceForm";
import { isLegalAcceptanceComplete } from "../config/legalVersions";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_STRUTTURA = [
  "RSA",
  "Casa di Riposo",
  "Comunità Alloggio",
  "Centro Diurno",
  "Residenza Protetta",
  "Hospice",
  "Altro",
];

const TIER_OPTIONS = [
  { id: "tier_1", label: "1 Struttura", strutture: 1 },
  { id: "tier_2", label: "2 Strutture", strutture: 2 },
  { id: "tier_3", label: "3 Strutture", strutture: 3 },
  { id: "tier_4", label: "4 Strutture", strutture: 4 },
  { id: "tier_5", label: "5 Strutture", strutture: 5 },
  { id: "tier_6", label: "6 Strutture", strutture: 6 },
  { id: "tier_7", label: "7 Strutture", strutture: 7 },
  { id: "tier_8", label: "8 Strutture", strutture: 8 },
  { id: "tier_9", label: "9 Strutture", strutture: 9 },
  { id: "tier_10", label: "10 Strutture", strutture: 10 },
  { id: "tier_unlimited", label: "10+ Strutture", strutture: 999 },
];

const TRIAL_STEPS = [
  { icon: User, title: "Registrati", text: "Crea il tuo account e attiva la prova gratuita." },
  { icon: Calendar, title: "Usa Affidya", text: "Hai 14 giorni per esplorare tutte le funzionalità." },
  { icon: Bell, title: "Promemoria", text: "Ti avviseremo prima della scadenza della prova." },
  { icon: Rocket, title: "Scegli il piano", text: "Effettua l'upgrade e continua senza interruzioni." },
];

export default function RegisterTrial() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showMultiStrutturaModal, setShowMultiStrutturaModal] = useState(false);
  const [formData, setFormData] = useState({
    organizationName: "",
    tipoStruttura: "",
    postiTotali: "",
    telefonoStruttura: "",
    comune: "",
    via: "",
    numeroCivico: "",
    provincia: "",
    tariffaDoppiaWc: "",
    tariffaSingolaWc: "",
    nomeReferente: "",
    telefonoReferente: "",
    adminEmail: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    plan_tier: "tier_1",
    acceptedTerms: false,
    acceptedClauses1341: false,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.organizationName.trim()) newErrors.organizationName = "Nome struttura obbligatorio";
    if (!formData.tipoStruttura) newErrors.tipoStruttura = "Seleziona il tipo struttura";
    if (!formData.postiTotali || parseInt(formData.postiTotali, 10) <= 0) newErrors.postiTotali = "Posti totali obbligatorio";
    if (!formData.telefonoStruttura.trim()) newErrors.telefonoStruttura = "Telefono obbligatorio";
    if (!formData.comune.trim()) newErrors.comune = "Comune obbligatorio";
    if (!formData.via.trim()) newErrors.via = "Via obbligatoria";
    if (!formData.numeroCivico.trim()) newErrors.numeroCivico = "Numero civico obbligatorio";
    if (!formData.provincia.trim()) newErrors.provincia = "Provincia obbligatoria";
    const tariffaDoppia = parseFloat(formData.tariffaDoppiaWc);
    const tariffaSingola = parseFloat(formData.tariffaSingolaWc);
    if (formData.tariffaDoppiaWc === "" || isNaN(tariffaDoppia) || tariffaDoppia < 0) {
      newErrors.tariffaDoppiaWc = "Inserisci un importo ≥ 0";
    }
    if (formData.tariffaSingolaWc === "" || isNaN(tariffaSingola) || tariffaSingola < 0) {
      newErrors.tariffaSingolaWc = "Inserisci un importo ≥ 0";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.nomeReferente.trim()) newErrors.nomeReferente = "Nome referente obbligatorio";
    if (!formData.telefonoReferente.trim()) newErrors.telefonoReferente = "Telefono referente obbligatorio";
    if (!formData.adminEmail.trim()) newErrors.adminEmail = "Email obbligatoria";
    else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) newErrors.adminEmail = "Email non valida";
    if (formData.adminEmail !== formData.confirmEmail) newErrors.confirmEmail = "Le email non coincidono";
    if (!formData.password) newErrors.password = "Password obbligatoria";
    else if (formData.password.length < 6) newErrors.password = "Password minimo 6 caratteri";
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Le password non coincidono";
    if (!formData.acceptedTerms) newErrors.acceptedTerms = "Devi accettare i Termini di Utilizzo";
    if (!formData.acceptedClauses1341) newErrors.acceptedClauses1341 = "Devi approvare le clausole ex artt. 1341-1342 c.c.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    setLoading(true);
    try {
      const payload = {
        organizationName: formData.organizationName,
        tipoStruttura: formData.tipoStruttura,
        postiTotali: parseInt(formData.postiTotali, 10),
        telefonoStruttura: formData.telefonoStruttura,
        comune: formData.comune,
        via: formData.via,
        numeroCivico: formData.numeroCivico,
        provincia: formData.provincia,
        tariffaDoppiaWc: parseFloat(formData.tariffaDoppiaWc),
        tariffaSingolaWc: parseFloat(formData.tariffaSingolaWc),
        nomeReferente: formData.nomeReferente,
        telefonoReferente: formData.telefonoReferente,
        adminEmail: formData.adminEmail,
        password: formData.password,
        plan_tier: formData.plan_tier,
        acceptedTerms: formData.acceptedTerms,
        acceptedClauses1341: formData.acceptedClauses1341,
      };
      const response = await axios.post(`${API_URL}/api/auth/register-trial`, payload);
      if (response.data.success) {
        toast.success("Prova gratuita attivata! Accedi con le tue credenziali.");
        navigate("/login?trial=activated");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  const selectedTier = TIER_OPTIONS.find((t) => t.id === formData.plan_tier) || TIER_OPTIONS[0];

  return (
    <div className="min-h-screen overflow-hidden bg-[#f7faf9] px-4 py-6 text-slate-900">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex flex-col gap-4 rounded-[2rem] border border-white/80 bg-white/85 p-5 shadow-lg sm:p-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Link to="/login" className="mb-4 inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:border-emerald-200 hover:text-emerald-700">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Torna al login
            </Link>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                <Gift className="h-5 w-5" />
              </span>
              <div>
                <h1 className="text-2xl font-bold text-slate-950">Prova gratuita di 14 giorni</h1>
                <p className="text-sm text-slate-600">Nessuna carta richiesta — attiva la demo e scopri Affidya</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl bg-emerald-600 px-5 py-4 text-white">
            <p className="text-sm font-semibold">14 giorni gratuiti</p>
            <p className="text-xs text-emerald-100">Accesso completo a tutte le funzionalità</p>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-center gap-3">
          <div className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold ${step >= 1 ? "bg-emerald-50 text-emerald-700" : "bg-white text-slate-400"}`}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-full ${step >= 1 ? "bg-emerald-600 text-white" : "bg-slate-200"}`}>1</div>
            <span>Struttura</span>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-300" />
          <div className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold ${step >= 2 ? "bg-emerald-50 text-emerald-700" : "bg-white text-slate-400"}`}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-full ${step >= 2 ? "bg-emerald-600 text-white" : "bg-slate-200"}`}>2</div>
            <span>Account</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <Card className="rounded-[2rem] border bg-white shadow-lg">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-emerald-600" />
                  Dati struttura principale
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-6">
                <div>
                  <Label>Nome struttura *</Label>
                  <Input value={formData.organizationName} onChange={(e) => handleChange("organizationName", e.target.value)} className={errors.organizationName ? "border-red-500" : ""} />
                  {errors.organizationName && <p className="mt-1 text-sm text-red-500">{errors.organizationName}</p>}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Tipo struttura *</Label>
                    <Select value={formData.tipoStruttura} onValueChange={(v) => handleChange("tipoStruttura", v)}>
                      <SelectTrigger className={errors.tipoStruttura ? "border-red-500" : ""}>
                        <SelectValue placeholder="Seleziona..." />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPI_STRUTTURA.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Posti totali *</Label>
                    <Input type="number" min="1" value={formData.postiTotali} onChange={(e) => handleChange("postiTotali", e.target.value)} />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Telefono struttura *</Label>
                    <Input value={formData.telefonoStruttura} onChange={(e) => handleChange("telefonoStruttura", e.target.value)} />
                  </div>
                  <div>
                    <Label>Comune *</Label>
                    <Input value={formData.comune} onChange={(e) => handleChange("comune", e.target.value)} />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="md:col-span-2">
                    <Label>Via *</Label>
                    <Input value={formData.via} onChange={(e) => handleChange("via", e.target.value)} />
                  </div>
                  <div>
                    <Label>N. civico *</Label>
                    <Input value={formData.numeroCivico} onChange={(e) => handleChange("numeroCivico", e.target.value)} />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label>Provincia *</Label>
                    <Input value={formData.provincia} onChange={(e) => handleChange("provincia", e.target.value)} />
                  </div>
                  <div>
                    <Label>€ Doppia WC *</Label>
                    <Input type="number" min="0" step="0.01" value={formData.tariffaDoppiaWc} onChange={(e) => handleChange("tariffaDoppiaWc", e.target.value)} />
                  </div>
                  <div>
                    <Label>€ Singola WC *</Label>
                    <Input type="number" min="0" step="0.01" value={formData.tariffaSingolaWc} onChange={(e) => handleChange("tariffaSingolaWc", e.target.value)} />
                  </div>
                </div>
                <Button type="button" onClick={() => validateStep1() && (setStep(2), window.scrollTo(0, 0))} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  Continua
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          )}

          {step === 2 && (
            <Card className="rounded-[2rem] border bg-white shadow-lg">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-3">
                  <User className="h-5 w-5 text-emerald-600" />
                  Referente e account
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 p-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Nome referente *</Label>
                    <Input value={formData.nomeReferente} onChange={(e) => handleChange("nomeReferente", e.target.value)} />
                  </div>
                  <div>
                    <Label>Telefono referente *</Label>
                    <Input value={formData.telefonoReferente} onChange={(e) => handleChange("telefonoReferente", e.target.value)} />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Email amministratore *</Label>
                    <Input type="email" value={formData.adminEmail} onChange={(e) => handleChange("adminEmail", e.target.value)} />
                  </div>
                  <div>
                    <Label>Conferma email *</Label>
                    <Input type="email" value={formData.confirmEmail} onChange={(e) => handleChange("confirmEmail", e.target.value)} />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Password *</Label>
                    <Input type="password" value={formData.password} onChange={(e) => handleChange("password", e.target.value)} />
                  </div>
                  <div>
                    <Label>Conferma password *</Label>
                    <Input type="password" value={formData.confirmPassword} onChange={(e) => handleChange("confirmPassword", e.target.value)} />
                  </div>
                </div>

                <div className="space-y-3 border-t pt-4">
                  <h3 className="font-semibold text-slate-700">Quante strutture vuoi gestire?</h3>
                  <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-2xl font-bold">{selectedTier.strutture === 999 ? "10+" : selectedTier.strutture}</div>
                    <div className="flex-1">
                      <p className="font-medium">{selectedTier.label}</p>
                      <p className="text-xs text-emerald-700">Durante la prova puoi creare fino a questo limite</p>
                    </div>
                    <Check className="h-5 w-5 text-emerald-600" />
                  </div>
                  <button type="button" onClick={() => setShowMultiStrutturaModal(true)} className="text-sm font-medium text-emerald-700 hover:underline">
                    {formData.plan_tier === "tier_1" ? "Gestisci più di una struttura?" : "Cambia numero strutture"}
                  </button>
                </div>

                <LegalAcceptanceForm
                  values={{
                    acceptedTerms: formData.acceptedTerms,
                    acceptedClauses1341: formData.acceptedClauses1341,
                  }}
                  onChange={handleChange}
                  errors={errors}
                  compact
                />

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">Indietro</Button>
                  <Button type="submit" disabled={loading || !isLegalAcceptanceComplete(formData)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Attiva prova gratuita"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </form>

        <Card className="mt-6 rounded-[2rem] border bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">Come funziona la prova gratuita di 14 giorni</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
            {TRIAL_STEPS.map((item) => (
              <div key={item.title} className="rounded-xl border border-slate-100 p-4">
                <item.icon className="mb-2 h-6 w-6 text-emerald-600" />
                <p className="font-semibold text-slate-900">{item.title}</p>
                <p className="mt-1 text-xs text-slate-600">{item.text}</p>
              </div>
            ))}
          </CardContent>
          <div className="mx-6 mb-6 flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50 p-4">
            <Shield className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <p className="text-sm text-slate-700">
              Nessun impegno, disdici quando vuoi. Durante i 14 giorni puoi testare tutte le funzionalità senza limiti.
            </p>
          </div>
        </Card>

        <Dialog open={showMultiStrutturaModal} onOpenChange={setShowMultiStrutturaModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Quante strutture vuoi gestire?</DialogTitle>
              <DialogDescription>Durante la prova gratuita potrai creare strutture fino al limite scelto.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3 py-2 sm:grid-cols-4">
              {TIER_OPTIONS.map((tier) => (
                <button
                  key={tier.id}
                  type="button"
                  className={`rounded-xl border-2 p-3 text-center transition ${
                    formData.plan_tier === tier.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => {
                    handleChange("plan_tier", tier.id);
                    setShowMultiStrutturaModal(false);
                  }}
                >
                  <div className="text-xl font-bold">{tier.strutture === 999 ? "10+" : tier.strutture}</div>
                  <p className="text-xs text-slate-600">strutture</p>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
